/**
 * The Announcements tab: what the issuer told the exchange.
 *
 * This is source material, not parser output — captured from the Saudi
 * Exchange by `capture/company_announcements.py`. It earns a place next to
 * the parsed filings for two reasons: it is the corroborating side of the
 * cross-check, so a reader following a finding wants to see the whole
 * announcement it came from; and for the results filings it carries a small
 * figure grid that is, on its own, a clean quarterly series going back
 * decades.
 *
 * A results announcement carries two grids and they are different periods —
 * `quarter` is the discrete three months, `period` is the cumulative
 * year-to-date. They are shown as separate blocks rather than merged,
 * because a reader who conflates them has the wrong number.
 *
 * Every row links out to the announcement in both languages. The Arabic page
 * is the one whose labels match an Arabic filing, so it is not a second-class
 * link here.
 */
import { useQuery } from '@tanstack/react-query'
import { useMemo, useState } from 'react'
import { ExternalLink } from 'lucide-react'

import type {
  AnnouncementRow, CompanyAnnouncementsResponse,
} from '@server/sql/announcements.ts'
import { ApiError, api, keys } from '~/lib/api.ts'
import { useT } from '~/i18n/index.tsx'
import { count, date, money } from '~/i18n/format.ts'
import { Badge, Skeleton } from '~/components/ui/primitives.tsx'
import { EmptyState, ErrorState } from '~/components/ui/empty.tsx'
import { Disclosure } from '~/components/ui/disclosure.tsx'
import { Table, Td, Th, Tr } from '~/components/ui/table.tsx'
import { Raw } from '~/components/ui/raw.tsx'
import { Segmented } from '~/components/ui/tabs.tsx'
import { cn } from '~/lib/cn.ts'

type Scope = 'financial' | 'all'

export function CompanyAnnouncements({ symbol }: { symbol: string }) {
  const { t, locale } = useT()
  const [scope, setScope] = useState<Scope>('financial')
  const [q, setQ] = useState('')

  const { data, error, refetch } = useQuery({
    queryKey: keys.companyAnnouncements(symbol),
    queryFn: () => api<CompanyAnnouncementsResponse>(
      `/api/companies/${encodeURIComponent(symbol)}/announcements`),
  })

  const rows = useMemo(() => {
    if (!data) return []
    const needle = q.trim().toLowerCase()
    return data.rows.filter((r) => {
      if (scope === 'financial' && !r.isFinancial) return false
      if (!needle) return true
      return [r.titleEn, r.titleAr, r.quarter, r.formTypeLabel, String(r.anId)]
        .some((x) => x?.toLowerCase().includes(needle))
    })
  }, [data, scope, q])

  if (error) {
    const e = error as ApiError
    return <ErrorState title={t('common.error')} detail={e.message} requestId={e.requestId}
      onRetry={() => void refetch()} retryLabel={t('action.retry')} />
  }
  if (!data) return <Skeleton className="h-96 w-full" />
  if (data.summary.total === 0)
    return <EmptyState title={t('ann.none')}>{t('ann.noneDetail')}</EmptyState>

  const s = data.summary
  return (
    <div className="space-y-5">
      <section className="grid gap-3 sm:grid-cols-3 lg:grid-cols-5">
        <Stat label={t('ann.total')} value={count(s.total, locale)} />
        <Stat label={t('ann.financial')} value={count(s.financial, locale)}
              hint={t('ann.financialHint')} />
        <Stat label={t('ann.figures')} value={count(s.figures, locale)} />
        <Stat label={t('ann.span')}
              value={`${date(s.firstPublished, locale)} – ${date(s.lastPublished, locale)}`} />
        {/* More than one multiplier across a company's filings is worth
            surfacing: it is exactly the condition under which an assumed
            scale silently corrupts a figure by a factor of a thousand. */}
        <Stat label={t('ann.units')}
              value={s.units.length ? s.units.map((u) => count(u, locale)).join(' · ') : '—'}
              hint={s.units.length > 1 ? t('ann.unitsVary') : undefined}
              warn={s.units.length > 1} />
      </section>

      <div className="flex flex-wrap items-center gap-3">
        <Segmented
          value={scope}
          onChange={setScope}
          options={[
            { id: 'financial' as const, label: t('ann.scope.financial') },
            { id: 'all' as const, label: t('ann.scope.all') },
          ]}
        />
        <input
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder={t('ann.filter')}
          className="ms-auto w-64 rounded-[var(--radius-control)] border border-[var(--border)]
                     bg-[var(--bg-base)] px-2.5 py-1.5 text-[length:var(--text-data)]
                     focus:border-[var(--accent)] focus:outline-none"
        />
      </div>

      {data.truncated && (
        <p className="text-[length:var(--text-micro)] text-[var(--warn)]">{t('ann.truncated')}</p>
      )}

      {rows.length === 0 ? (
        <EmptyState title={t('empty.generic')} />
      ) : (
        <div className="space-y-2">
          {rows.map((r) => <Announcement key={r.anId} row={r} />)}
        </div>
      )}
    </div>
  )
}

function Stat({ label, value, hint, warn }: {
  label: string; value: string; hint?: string; warn?: boolean
}) {
  return (
    <div className="rounded-[var(--radius-panel)] border border-[var(--border)]
                    bg-[var(--bg-panel)] px-3 py-2.5">
      <div className="text-[length:var(--text-micro)] uppercase tracking-[0.04em]
                      text-[var(--text-muted)]">{label}</div>
      <div className={cn('tnum mt-0.5 text-[length:var(--text-title)] font-semibold',
        warn && 'text-[var(--warn)]')}>{value}</div>
      {hint && <div className="text-[length:var(--text-micro)] text-[var(--text-faint)]">{hint}</div>}
    </div>
  )
}

function Announcement({ row }: { row: AnnouncementRow }) {
  const { t, locale } = useT()
  const grids = useMemo(() => {
    const byGrid = new Map<string, AnnouncementRow['figures']>()
    for (const f of row.figures) {
      if (!byGrid.has(f.grid)) byGrid.set(f.grid, [])
      byGrid.get(f.grid)!.push(f)
    }
    return [...byGrid.entries()]
  }, [row.figures])

  const header = (
    <div className="flex min-w-0 flex-wrap items-center gap-2">
      {row.isFinancial && <Badge state="accent">{row.kind === 'annual'
        ? t('ann.kind.annual') : t('ann.kind.interim')}</Badge>}
      {row.quarter && <Badge state="neutral">{row.quarter}</Badge>}
      {row.isCorrection && <Badge state="warn">{t('ann.correction')}</Badge>}
      <span className="min-w-0 flex-1 truncate text-[length:var(--text-data)]">
        {row.titleEn ?? <Raw>{row.titleAr ?? ''}</Raw>}
      </span>
      <span className="tnum shrink-0 whitespace-nowrap text-[length:var(--text-micro)]
                       text-[var(--text-muted)]">
        {date(row.publishedAt, locale)}
      </span>
    </div>
  )

  return (
    <Disclosure summary={header} count={row.figures.length || null}>
      <div className="space-y-3">
        {/* the citation, always both languages */}
        <div className="flex flex-wrap items-center gap-x-4 gap-y-1
                        text-[length:var(--text-micro)] text-[var(--text-muted)]">
          <span className="tnum font-[family-name:var(--font-mono)]">
            {t('crosscheck.anId')} {row.anId}
          </span>
          {row.formTypeLabel && <span>{row.formTypeLabel}</span>}
          {row.hijriDate && <span>{row.hijriDate}</span>}
          {row.unitsMultiplier !== null && (
            <span>{t('ann.unit')}: {count(row.unitsMultiplier, locale)}</span>
          )}
          {row.templateEra && <span>{t('ann.era')}: {row.templateEra}</span>}
          {row.parseStatus && row.parseStatus !== 'ok' && (
            <Badge state="warn">{row.parseStatus}</Badge>
          )}
          <span className="ms-auto inline-flex items-center gap-2">
            {row.urlEn && (
              <a href={row.urlEn} target="_blank" rel="noopener noreferrer" className="link-inline">
                {t('ann.openEn')}<ExternalLink className="inline size-3 align-[-1px]" />
              </a>
            )}
            {row.urlAr && (
              <a href={row.urlAr} target="_blank" rel="noopener noreferrer" className="link-inline">
                {t('ann.openAr')}<ExternalLink className="inline size-3 align-[-1px]" />
              </a>
            )}
          </span>
        </div>

        {row.titleAr && (
          <Raw lang="ar" className="block text-[length:var(--text-data)] text-[var(--text-muted)]">
            {row.titleAr}
          </Raw>
        )}

        {grids.length === 0 ? (
          <p className="text-[length:var(--text-data)] text-[var(--text-faint)]">
            {t('ann.noFigures')}
          </p>
        ) : (
          grids.map(([grid, figures]) => (
            <div key={grid}>
              <div className="mb-1 flex items-baseline gap-2">
                <h5 className="text-[length:var(--text-data)] font-semibold">
                  {t(`crosscheck.gridName.${grid}` as never)}
                </h5>
                <span className="text-[length:var(--text-micro)] text-[var(--text-muted)]">
                  {grid === 'quarter' ? t('ann.gridHint.quarter')
                    : grid === 'period' ? t('ann.gridHint.period')
                    : grid === 'annual' ? t('ann.gridHint.annual') : ''}
                </span>
              </div>
              <Table>
                <thead>
                  <Tr>
                    <Th>{t('ann.item')}</Th>
                    <Th numeric>{t('ann.value')}</Th>
                    <Th numeric>{t('ann.priorYear')}</Th>
                    <Th numeric>{t('ann.change')}</Th>
                    {grid === 'quarter' && <Th numeric>{t('ann.priorQuarter')}</Th>}
                    {grid === 'quarter' && <Th numeric>{t('ann.changeQ')}</Th>}
                  </Tr>
                </thead>
                <tbody>
                  {figures.map((f) => (
                    <Tr key={`${f.grid}-${f.sortOrder}`}>
                      <Td>
                        <span className="block">{f.labelEn ?? '—'}</span>
                        {f.labelAr && (
                          <Raw lang="ar" className="block text-[length:var(--text-micro)]
                                                    text-[var(--text-muted)]">{f.labelAr}</Raw>
                        )}
                      </Td>
                      <Td numeric className="font-medium">{money(f.valueSar ?? f.value, locale)}</Td>
                      <Td numeric className="text-[var(--text-muted)]">
                        {money(f.valuePriorYear, locale)}
                      </Td>
                      <Td numeric className={cn(
                        f.pctChange === null ? 'text-[var(--text-faint)]'
                          : f.pctChange >= 0 ? 'text-[var(--pass)]' : 'text-[var(--fail)]')}>
                        {f.pctChange === null ? '—' : `${f.pctChange.toFixed(2)}%`}
                      </Td>
                      {grid === 'quarter' && (
                        <Td numeric className="text-[var(--text-muted)]">
                          {money(f.valuePriorQuarter, locale)}
                        </Td>
                      )}
                      {grid === 'quarter' && (
                        <Td numeric className={cn(
                          f.pctChangePriorQuarter === null ? 'text-[var(--text-faint)]'
                            : f.pctChangePriorQuarter >= 0
                              ? 'text-[var(--pass)]' : 'text-[var(--fail)]')}>
                          {f.pctChangePriorQuarter === null
                            ? '—' : `${f.pctChangePriorQuarter.toFixed(2)}%`}
                        </Td>
                      )}
                    </Tr>
                  ))}
                </tbody>
              </Table>
            </div>
          ))
        )}
      </div>
    </Disclosure>
  )
}
